import React, { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { usePublicBranding } from "../../hooks/usePublicBranding";
import { getPortalSubdomain } from "../../utils/portalSubdomain";
import defaultBrandLogo from "../../assets/logo.png";
import Spinner from "../../components/ui/Spinner";

const PortalLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const advisorName = searchParams.get("advisor")?.trim() || "";
  const referralFromUrl = searchParams.get("ref")?.trim() || "";
  const subdomain = getPortalSubdomain();
  const { branding, loading: brandingLoading } = usePublicBranding(subdomain);
  const { login, isAuthenticated, user } = usePortalAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const businessLabel = branding?.businessName || branding?.companyName || advisorName || user?.operatorBusinessName;
  const chatbotLabel = branding?.chatbotName || "your advisor’s assistant";

  if (isAuthenticated && user) {
    return <Navigate to="/portal/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password, referralFromUrl || null);
      navigate("/portal/dashboard", { replace: true });
    } catch {
      setError("We couldn’t sign you in. Check your email and password, or contact your credit advisor.");
    } finally {
      setSubmitting(false);
    }
  };

  const logoUrl = branding?.logoUrl;

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-slate-50">
      <header className="border-b border-white/10 bg-[var(--portal-primary)]">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4">
          <div className="flex min-w-0 items-center gap-3">
            {logoUrl ? (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-white/5">
                <img src={logoUrl} alt="" className="max-h-full max-w-full object-contain" />
              </div>
            ) : null}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white">
                {businessLabel || "Client portal"}
              </p>
              <p className="text-xs text-white/80">Sign in</p>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 flex-col items-center justify-center px-4 pb-16 pt-12 sm:px-6">
        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:mx-auto sm:p-8">
          <div className="mb-8 text-center">
            {logoUrl ? (
              <div className="mx-auto mb-4 flex max-h-20 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white px-2 py-2">
                <img src={logoUrl} alt="" className="max-h-20 w-auto max-w-full object-contain" />
              </div>
            ) : (
              <img
                src={defaultBrandLogo}
                alt="IntelliWarrior"
                className="mx-auto mb-4 h-20 w-auto max-w-[280px] object-contain"
              />
            )}
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-600">Client portal</p>
            {businessLabel ? (
              <>
                <p className="mt-2 text-sm font-medium text-slate-800">{businessLabel}</p>
                {branding?.tagline ? <p className="mt-1 text-sm text-[var(--portal-accent)]">{branding.tagline}</p> : null}
                <h1 className="mt-1 text-2xl font-semibold text-navy-900">Welcome back</h1>
              </>
            ) : (
              <h1 className="mt-2 text-2xl font-semibold text-navy-900">Welcome back</h1>
            )}
            <p className="mt-2 text-sm text-slate-600">
              Sign in with the email your advisor has on file for your repair program.
            </p>
            {!brandingLoading && branding?.chatbotWelcomeMessage && (
              <p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                {branding.chatbotWelcomeMessage}
              </p>
            )}
            {branding?.aiChatbotEnabled !== false ? (
              <p className="mt-2 text-[11px] text-slate-400">
                Questions later? Chat with <span className="font-medium text-[var(--portal-accent)]">{chatbotLabel}</span> after you sign in.
              </p>
            ) : null}
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-sm text-[#991B1B]">{error}</div>
          )}

          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div>
              <label htmlFor="portal-email" className="block text-sm font-medium text-slate-800">
                Email
              </label>
              <input
                id="portal-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-slate-800 shadow-sm outline-none placeholder:text-slate-400 focus:border-navy-600 focus:ring-2 focus:ring-navy-600/30"
                placeholder="you@email.com"
                required
              />
            </div>
            <div>
              <label htmlFor="portal-password" className="block text-sm font-medium text-slate-800">
                Password
              </label>
              <input
                id="portal-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-slate-800 shadow-sm outline-none placeholder:text-slate-400 focus:border-navy-600 focus:ring-2 focus:ring-navy-600/30"
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[var(--portal-primary)] py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:opacity-90 disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Spinner className="h-4 w-4 border-2 border-white/30 border-t-white" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>
      </div>

      <footer className="border-t border-slate-200 bg-white py-4 text-center text-xs text-slate-400">
        {branding?.businessName || branding?.companyName
          ? `© ${branding.businessName || branding.companyName}`
          : "Powered by IntelliWarrior"}
      </footer>
    </div>
  );
};

export default PortalLoginPage;
